import { Icon, IconButton, Stack, tokens } from '@/design';
import type { MapTool } from '@/state/useViewerStore';

interface FloatingMapToolbarProps {
  activeTool: MapTool;
  onToolChange(tool: MapTool): void;
  onFitVisibleLayers(): void;
  onOpenLayers(): void;
  onZoomIn(): void;
  onZoomOut(): void;
}

interface ToolItem {
  id: MapTool;
  icon: 'near_me' | 'pan_tool' | 'straighten' | 'polyline';
  label: string;
  shortcut: string;
}

const TOOL_ITEMS: ReadonlyArray<ToolItem> = [
  { id: 'select', icon: 'near_me', label: 'Select', shortcut: 'S' },
  { id: 'pan', icon: 'pan_tool', label: 'Pan', shortcut: 'P' },
  { id: 'measure', icon: 'straighten', label: 'Measure', shortcut: 'M' },
  { id: 'area', icon: 'polyline', label: 'Area', shortcut: 'A' },
] as const;

export function FloatingMapToolbar({
  activeTool,
  onToolChange,
  onFitVisibleLayers,
  onOpenLayers,
  onZoomIn,
  onZoomOut,
}: FloatingMapToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Map tools"
      style={{
        position: 'absolute',
        top: tokens.space[2],
        right: tokens.space[2],
        zIndex: tokens.zIndex.panel,
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        padding: tokens.space[1],
      }}
    >
      <Stack direction="row" align="center" gap={1}>
        {TOOL_ITEMS.map((item) => (
          <IconButton
            key={item.id}
            aria-label={`${item.label} tool (${item.shortcut})`}
            onClick={() => onToolChange(item.id)}
            style={{
              background: activeTool === item.id ? tokens.color.surface[3] : 'transparent',
            }}
          >
            <Icon name={item.icon} size={20} decorative />
          </IconButton>
        ))}
        <IconButton aria-label="Zoom in (+)" onClick={onZoomIn}>
          <Icon name="zoom_in" size={20} decorative />
        </IconButton>
        <IconButton aria-label="Zoom out (-)" onClick={onZoomOut}>
          <Icon name="zoom_out" size={20} decorative />
        </IconButton>
        <IconButton aria-label="Fit visible layers (F)" onClick={onFitVisibleLayers}>
          <Icon name="center_focus_strong" size={20} decorative />
        </IconButton>
        <IconButton aria-label="Focus layer panel (L)" onClick={onOpenLayers}>
          <Icon name="layers" size={20} decorative />
        </IconButton>
      </Stack>
    </div>
  );
}
